/* eslint-disable react/no-unescaped-entities */
import React, { useEffect, useState } from 'react'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import { Typography, useTheme } from '@mui/material'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import { useNavigate } from 'react-router-dom'
import CustomModal from '../../../components/CustomModal'
import { StyledSaveMenuWrapper } from './styles'
import ROUTES from '../../../consts/paths'
import { useAppDispatch } from '../../../store'
import GenericButton from '../../../components/GenericButton'
import { ButtonSize, ButtonType } from '../../../components/GenericButton/styles'
import {
  getActionLoading,
  getEditableQuestion,
  getEditableSurvey,
  getSurveySections,
  getSurveyTitle
} from '../../../store/slices/SurveyTemplateSlice/selectors'
import { Messages, type Question, SurveyItemQuestionType } from '../types'
import { type ISectionSettings, type ISurveySection, resetState } from '../../../store/slices/SurveyTemplateSlice'
import { createSurveyTemplate, deleteSurveyTemplateApi, editSurveyTemplate, editSurveyTemplateConfiramtion } from '../../../store/slices/SurveyTemplateSlice/apis'
import { refreshEditableSurveyBaseline } from '../templateUpdateRefresh'
import { notification } from '../../../components/Notifications'
import { checkQuestionIsDirtyAgainstBaseline, findDuplicateQuestionInSection, findInvalidQuestion, findInvalidSection, scrollToElement } from '../helpers'
import _ from 'lodash'
import { type ISurveySection as ITemplateSurveySection } from '../../../models/SurveyTemplate'
import GenericModal from '../../../components/GenericModal'
import { transformSections, validateDataFormatForSections } from '../../SurveyFeature/components/SurveyFeatureStepper/utils'
import { type ApiError } from '../../../models/Common'
import { useSlotBackgroundTask } from '../../../hooks/useSlotBackgroundTask'
import { notifySlotTaskTerminal } from '../../../utils/notifySlotTaskTerminal'

interface SurveyHeaderProps {
  isDirty: boolean
  setIsDirty?: (value: boolean) => void
}

// Shapes section settings for the API. Branched sections serialize their own
// repeat-per-instructor/selected-roles settings N times (once per parent settings group),
// copying the parent's attributes at each index so the backend loop has the right count.
export const sanitizeSectionSettingsForPayload = (section: ISurveySection, parentSettings?: ISectionSettings[]): Array<Record<string, any>> | undefined => {
  const settings = Array.isArray(section.settings) ? section.settings : []

  if (section.parentSurveySectionId) {
    const sectionDefaultSetting = settings[0]
    const count = Math.max(1, parentSettings?.length ?? 1)
    const childRpi = sectionDefaultSetting?.isRepeatPerInstructor ?? false

    return Array.from({ length: count }, (_, i) => ({
      isRepeatPerInstructor: childRpi,
      ...(childRpi ? { selectedRoles: sectionDefaultSetting?.selectedRoles } : {}),
      attributes: parentSettings?.[i]?.attributes ?? [],
      isSelectInstructorEnabled: parentSettings?.[i]?.isSelectInstructorEnabled ?? false
    }))
  }

  return settings.map((setting) => (
    setting?.isRepeatPerInstructor
      ? setting
      : { ...setting, selectedRoles: undefined }
  ))
}

const SurveyHeader: React.FC<SurveyHeaderProps> = ({ isDirty, setIsDirty }): JSX.Element => {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null)
  const open = Boolean(anchorEl)
  const [updateConfirmationModal, setUpdateConfirmationModal] = useState<boolean>(false)
  const [isUpdateConfirmLoading, setIsUpdateConfirmLoading] = useState<boolean>(false)
  const [isDeleteModalOpen, setDeleteModalOpen] = React.useState<boolean>(false)
  const theme = useTheme()
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const surveyTitle = getSurveyTitle()
  const surveySections = getSurveySections()
  const editableQuestion = getEditableQuestion()
  const editableSurvey = getEditableSurvey()
  const isLoading = getActionLoading()
  const modalTitle = (surveyTitle === undefined || surveyTitle === '') ? '(No Title)' : surveyTitle
  // Tracks only the first task_id when a save fans out refreshes to multiple Draft Survey
  // Projects (task_ids) — approved decision, see 2026-08-13-background-task-202-contract spec.
  // editableSurvey is null on the Create Survey Template route (no id param, so
  // setSurveyByApi never dispatches setEditableSurvey) — '' is a safe no-op tracking key
  // since setTask is only ever invoked from submitEditableSurvey, which only runs once
  // editableSurvey is truthy.
  const { setTask } = useSlotBackgroundTask({
    slotId: editableSurvey?.templateId ? `template-${editableSurvey.templateId}` : '',
    onTerminal: notifySlotTaskTerminal
  })

  const handleBack = (): void => {
    navigate(ROUTES.SURVEYS)
    dispatch(resetState())
  }

  const checkIsSectionDirty = (section: ISurveySection, sectionIndex: number): boolean => {
    const areAllSectionEqual = _.isEqual(editableSurvey?.surveySections?.[sectionIndex], section)
    return !areAllSectionEqual
  }

  const checkIsSectionIsOld = (section: ISurveySection): boolean => {
    const t = editableSurvey.surveySections.find((s: ITemplateSurveySection) => s.surveySectionId === section.surveySectionId)
    if (t?.surveySectionId) {
      return true
    }
    return false
  }

  const checkQuestionIsDirty = (sectionIndex: number, newQuestion: Question): boolean => {
    return checkQuestionIsDirtyAgainstBaseline(
      editableSurvey?.surveySections?.[sectionIndex]?.surveyItems,
      newQuestion
    )
  }

  const prepareSurveySectionPayload = (section: ISurveySection, sectionIndex?: number): Record<string, any> => {
    const parentSection = section.parentSurveySectionId
      ? surveySections?.find((s: ISurveySection) => s.surveySectionId === section.parentSurveySectionId)
      : undefined
    const sanitizedSettings = sanitizeSectionSettingsForPayload(section, parentSection?.settings)
    const sectionDefaultSetting = sanitizedSettings?.[0]

    return {
      ...(sectionIndex !== undefined
        ? { isDirty: checkIsSectionIsOld(section) ? checkIsSectionDirty(section, sectionIndex) : undefined }
        : {}),
      name: section.name,
      // Keep the top-level flags aligned with the first settings group because the payload
      // still carries both representations today.
      isRepeatPerInstructor: sectionDefaultSetting?.isRepeatPerInstructor ?? section.isRepeatPerInstructor,
      isSelectInstructorEnabled: sectionDefaultSetting?.isSelectInstructorEnabled ?? section?.isSelectInstructorEnabled,
      settings: sanitizedSettings,
      title: section.title ?? undefined,
      descr: section.descr,
      surveySectionId: section.surveySectionId ?? undefined,
      attributes: section.attributes,
      branchingFilters: section.branchingFilters,
      indentation: section.indentation,
      parentSurveySectionId: section.parentSurveySectionId,
      surveyItems: section.surveyItems?.map((question: Question) => ({
        ...(sectionIndex !== undefined
          ? { isDirty: question.itemId && checkQuestionIsDirty(sectionIndex, question) }
          : {}),
        itemId: question.itemId ?? undefined,
        itemType: question.itemType,
        prompt: question.prompt,
        isRequired: question.isRequired,
        branchingFilters: question?.branchingFilters,
        isSchoolApproved: question.isSchoolApproved,
        ...(question.itemType === SurveyItemQuestionType.MULTIPLE_CHOICE ||
          question.itemType === SurveyItemQuestionType.LINEAR_SCALE ||
          question.itemType === SurveyItemQuestionType.MULTI_SELECT
          ? { object: question.object }
          : {})
      }))
    }
  }

  const submitEditableSurvey = async (): Promise<void> => {
    const preparedData = {
      title: surveyTitle,
      isDirty,
      surveySections: surveySections?.map((section: ISurveySection, sectionIndex: number) => prepareSurveySectionPayload(section, sectionIndex))
    }

    const surveyTemplateData = {
      urlId: editableSurvey.templateId,
      data: preparedData
    }

    try {
      const response = await dispatch(editSurveyTemplate(surveyTemplateData)).unwrap()
      if (response?.surveyProjectTemplateChange) {
        setUpdateConfirmationModal(true)
      }
      // createApiThunk only returns response.data, not the HTTP status, so task_id's presence
      // (not status === 202) is the enqueued-work signal here — the Backend contract guarantees
      // task_id is only ever present when work was actually enqueued.
      if (response?.taskId) {
        setTask(response.taskId, response.message ?? 'Draft Survey Projects using this template are being refreshed.')
      }
      const templateId = response?.templateId ?? editableSurvey.templateId
      if (templateId) {
        try {
          await refreshEditableSurveyBaseline(dispatch, templateId)
        } catch (refreshError) {
          console.error('Error refreshing survey template baseline:', refreshError)
          notification('Template saved, but the latest version could not be reloaded. Refresh the page before making more edits.', { variant: 'warning' })
        }
      }
    } catch (error) {
      console.error('Error editing survey template:', error)
    }
  }

  const submitCreateSurvey = async (): Promise<any> => {
    try {
      const data = {
        title: surveyTitle,
        surveySections: surveySections?.map((section: ISurveySection) => prepareSurveySectionPayload(section))
      }
      const createdSurvey = await dispatch(createSurveyTemplate({ data })).unwrap()
      const createdTemplateId = createdSurvey?.templateId
      if (createdTemplateId) {
        // Navigate to the persisted template; SurveyTemplateFeature reloads via GET on id change.
        // School-approved forks may return new item_ids in the POST response — do not cache pre-save ids.
        setTimeout(() => { navigate(`${ROUTES.SURVEY_LIST}/${createdTemplateId as string}`) }, 0)
      }
    } catch (error) {
      console.error('Error creating survey template:', error)
    }
  }

  const handleSave = async (): Promise<void> => {
    if (!surveySections) return

    const invalidSection = findInvalidSection(surveySections)
    if (invalidSection) {
      notification(Messages.NO_QUESTION, { variant: 'error' })
      scrollToElement(invalidSection)
      return
    }

    const invalidQuestion = findInvalidQuestion(surveySections)
    if (invalidQuestion) {
      notification('Unable to save Survey Template', {
        variant: 'error',
        subTitle: 'Please fill in all required fields and check for duplicate question options.'
      })
      scrollToElement(invalidQuestion)
      return
    }

    // CR-213: block same-section duplicate questions client-side (the backend rejects them
    // with a 422 too). Two identical-prompt/type questions in one section collapse onto a
    // single stored item, duplicating their responses/comments across both.
    const duplicateQuestion = findDuplicateQuestionInSection(surveySections)
    if (duplicateQuestion) {
      notification(Messages.DUPLICATE_QUESTION, { variant: 'error' })
      // Scroll to the offending card by its itemId (a client temp UUID for new questions);
      // the block above fires regardless, so a missing id never lets the save through.
      if (duplicateQuestion.itemId) scrollToElement(duplicateQuestion.itemId)
      return
    }

    const validity = validateDataFormatForSections(transformSections(surveySections as any))
    const falseId = Object.entries(validity).filter(([key, value]) => !value).map(([key, value]) => key)
    if (falseId.length > 0) {
      scrollToElement(falseId[0], 'red')
      notification('There is an error in the highlighted section or its child elements', { variant: 'error' })
      return
    }
    setIsDirty?.(false)
    const submitSurvey = editableSurvey ? submitEditableSurvey : submitCreateSurvey
    await submitSurvey()
  }

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>): void => {
    setAnchorEl(event.currentTarget)
  }

  const handleClose = (): void => {
    setAnchorEl(null)
  }

  const handleDeleteClick = (): void => {
    setAnchorEl(null)
    setDeleteModalOpen(true)
  }

  const handleDeleteConfirmed = async (): Promise<void> => {
    // delete survey template if templateId exists
    if (editableSurvey?.templateId) {
      try {
        await dispatch(deleteSurveyTemplateApi({ urlId: editableSurvey?.templateId })).unwrap()
      } catch (error) {
        const customError = error as ApiError
        if (customError && customError.status === 422) {
          notification(customError.message || 'Unknown error', { variant: 'error' })
          return
        }
        notification(customError?.message ?? 'Internal server error', { variant: 'error' })
      }
    }
    setDeleteModalOpen(false)
    setIsDirty?.(false)
    setTimeout(() => { navigate(ROUTES.SURVEYS) }, 0)
  }

  const handleConfirmationUpdateTemplate = async (): Promise<any> => {
    setIsUpdateConfirmLoading(true)
    try {
      const res = await dispatch(editSurveyTemplateConfiramtion({ urlId: editableSurvey.templateId }))
      if (res.meta?.requestStatus === 'fulfilled') {
        setUpdateConfirmationModal(false)
        notification('Updated in all Published Survey Projects', { variant: 'success' })
      }
    } catch (error) {
      console.error('Error creating survey template:', error)
    } finally {
      setIsUpdateConfirmLoading(false)
    }
  }

  return (
    <React.Fragment>
      <StyledSaveMenuWrapper>
        <GenericButton
          buttonType={ButtonType.Primary}
          buttonSize={ButtonSize.Medium}
          disabled={editableQuestion !== null || surveyTitle === ''}
          loading={isLoading}
          label={editableSurvey ? 'Save' : 'Create'}
          onClick={() => {
            void handleSave()
          }}
          style={{
            flexShrink: 0,
            [theme.breakpoints.down('md')]: {
              width: '100%'
            }
          }}
        />
        <GenericButton
          buttonType={ButtonType.Secondary}
          buttonSize={ButtonSize.Medium}
          icon={<MoreVertIcon />}
          onClick={handleClick}
          style={{
            flexShrink: 0,
            [theme.breakpoints.down('md')]: {
              width: '100% !important'
            }
          }}
        />
      </StyledSaveMenuWrapper>
      <Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
        <MenuItem onClick={handleDeleteClick}>Delete</MenuItem>
      </Menu>
      <GenericModal
        isOpen={isDeleteModalOpen}
        onClose={() => { setDeleteModalOpen(false) }}
        onConfirm={async () => { await handleDeleteConfirmed() }}
        title={`Delete "${modalTitle}"?`}
        confirmActionText="Delete"
        confirmActionType={ButtonType.Destructive}
        destructiveButtonText='Cancel'
        destructiveButtonType={ButtonType.Secondary}
        modalWidth={theme.spacing(136)}
      >
        You will not be able to recover this Survey Template. All references to it will be deleted.
      </GenericModal>
      <GenericModal
        isOpen={updateConfirmationModal}
        title={'Confirm: Template in Use'}
        onClose={() => { setUpdateConfirmationModal(false) }}
        maxWidth='sm'
        confirmButtonLoading={isUpdateConfirmLoading}
        onConfirm={handleConfirmationUpdateTemplate}
      >
        <Typography> This template is used in a published Survey Project. Changes won’t be reflected in these published Survey Projects until you confirm. </Typography>
      </GenericModal>

    </React.Fragment>
  )
}
export default SurveyHeader
